import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { isSplitDefined } from '@/api/getSplitDetails';
import { useCurrency } from '@/context/CurrencyContext/CurrencyContext';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useGetPaymentSummary } from '@/hooks/useGetPaymentSummary';
import { useSplitDetails } from '@/hooks/useSplitDetails';
import formatNumber from '@/utils/formatNumber';
import styles from './SuccessSummary.module.scss';
import { SuccessSummaryHead } from './SuccessSummaryHead';
import { SuccessSummaryRow } from './SuccessSummaryRow';
import { SuccessSummaryTotal } from './SuccessSummaryTotal';

const Skeleton = () => {
  const { t } = useTranslation();
  return <div>{t('successSummary.loading')}</div>;
};

export const SuccessSummary = () => {
  const { t } = useTranslation();
  const query = new URLSearchParams(useLocation().search);
  const orderNumber = query.get('orderNumber') || '';
  const tableNumber = query.get('tableNumber') || '';
  const counterNumber = query.get('counterNumber') || '';
  const outletId = query.get('outletId') || '';
  const numberOfSplits = Number(query.get('numberOfSplits')) || 0;
  // Equal splits carry numberOfSplits; items splits carry splitType instead
  // (see usePaymentNavigation) — both are partial payments
  const isSplitPayment = numberOfSplits > 0 || query.get('splitType') === 'split_by_items';
  const browserUserId = useBrowserUserId();
  const { currencySymbol } = useCurrency();
  const { data: paymentSummary } = useGetPaymentSummary({ orderNumber });
  const { data: splitDetails } = useSplitDetails({ orderNumber });

  // Status logic based on query params:
  // - No split (neither numberOfSplits nor splitType) → "Fully paid"
  // - Split payment (equal or by items) → check splitDetails status:
  //   - PENDING → "Partially paid"
  //   - COMPLETED or other → "Fully paid"
  const getStatusLabel = () => {
    if (!isSplitPayment) {
      return t('successSummary.fullyPaid');
    }
    // Split payment - check splitDetails
    if (!isSplitDefined(splitDetails)) {
      // Still loading or error - default to partially paid for split
      return t('successSummary.partiallyPaid');
    }
    return splitDetails.status === 'PENDING'
      ? t('successSummary.partiallyPaid')
      : t('successSummary.fullyPaid');
  };

  const statusLabel = getStatusLabel();
  const isPartiallyPaid = statusLabel === t('successSummary.partiallyPaid');

  useEffect(() => {
    if (paymentSummary && typeof window.gtag === 'function') {
      window.gtag('event', 'purchase', {
        ab_group: paymentSummary.userServiceFeeAmount > 0 ? 1 : 0,
        order_id: orderNumber,
        browserUserId,
        outlet_id: outletId,
        tips_amount: paymentSummary.userTipAmount,
        order_value: paymentSummary.orderAmount,
        service_fee_amount: paymentSummary.userServiceFeeAmount,
        payment_status: '/checkout/success',
      });
    }
  }, [paymentSummary, browserUserId, orderNumber, outletId]);

  return paymentSummary ? (
    <div className={styles.successSummary}>
      <SuccessSummaryHead
        isPartial={isPartiallyPaid}
        label={
          tableNumber
            ? t('successSummary.tableDineIn', { tableNumber })
            : counterNumber
              ? t('successSummary.counterNumber', { counterNumber })
              : ''
        }
        value={statusLabel}
      />

      {isSplitDefined(splitDetails) ? (
        <SuccessSummaryRow
          label={t('checkout.splitSummary.totalBill')}
          value={`${currencySymbol} ${formatNumber(splitDetails.totalAmount)}`}
        />
      ) : (
        <SuccessSummaryRow
          label={t('checkout.summary.subtotal')}
          value={`${currencySymbol} ${formatNumber(paymentSummary.orderAmount)}`}
        />
      )}

      <SuccessSummaryRow
        label={t('successSummary.tipAmount')}
        value={`${currencySymbol} ${formatNumber(paymentSummary.userTipAmount)}`}
      />

      {Boolean(paymentSummary?.userServiceFeeAmount) && paymentSummary.userServiceFeeAmount > 0 && (
        <SuccessSummaryRow
          label={t('successSummary.serviceFee')}
          value={`${currencySymbol} ${formatNumber(paymentSummary.userServiceFeeAmount)}`}
        />
      )}

      <SuccessSummaryTotal
        label={t('successSummary.youPaid')}
        value={`${currencySymbol} ${formatNumber(paymentSummary.userPaymentAmount)}`}
      />
    </div>
  ) : (
    <Skeleton />
  );
};
